import {
  FlexColumn,
  FuuzeColors,
  FuuzeText,
  FuuzeTooltip,
  FuuzeTooltipDefaultTrigger,
  filterSvgColorTo,
  LibraryIcon,
  mergeClassNames,
  useResize,
} from '@fuuze/shared';
import { useTranslation } from 'react-i18next';
import { IoWarning } from 'react-icons/io5';
import { LuAudioLines } from 'react-icons/lu';
import { PiCardsThreeBold } from 'react-icons/pi';
import { TbLayoutDashboardFilled } from 'react-icons/tb';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../../Hooks/use-user.hook';
import commonStyles from '../../styles/common.module.css';
import { FuuzeNavLink } from './fuuze-nav-link';
import { NavBarCreatorPodcastLink } from './links/nav-bar-creator-podcast-link';
import { SearchNavLink } from './links/nav-bar-search-link';
import { NavBarSettingsLink } from './links/nav-bar-settings-link';
import styles from './menu-content.module.css';
import { PodcastSelector } from './podcast-selector';
import { SignOutOrSignInButton } from './sign-out-or-sign-in';

export function MenuContent({ closeMenu }: { closeMenu: () => void }) {
  const { pathname } = useLocation();
  const { t, ready } = useTranslation('components:leftNav', { keyPrefix: 'menuContent', useSuspense: false });
  const user = useUser();
  const navigate = useNavigate();
  const [size] = useResize();

  const isUserCreator = (user.data?.ownedChannels.length || 0) > 0;

  const LibraryLink = () => (
    <FuuzeNavLink path='/library' active={pathname.includes('/library')} Icon={LibraryIcon} title={t('library')} />
  );

  const BecomeCreatorLink = () => (
    <NavLink style={{ textDecoration: 'none' }} to='/becomeCreator'>
      <div className={mergeClassNames(styles.BecomeCreatorLinkWrapper, commonStyles.GradientBorder)}>
        <img
          src='/images/logo-icon.webp'
          style={{ marginRight: 8, width: 16, height: 16, ...filterSvgColorTo('white') }}
        />
        <FuuzeText>{t('becomeACreator')}</FuuzeText>
      </div>
    </NavLink>
  );

  const CreatorDashboardLink = () => (
    <FuuzeNavLink
      path='/creator/dashboard'
      active={pathname.includes('/creator/dashboard')}
      Icon={TbLayoutDashboardFilled}
      title={
        user.data?.isSomeStripeRequirementsEventuallyDue() ? (
          <div className={commonStyles.FlexRow} style={{ gap: 8 }}>
            <FuuzeText as='span'>{t('dashboard')}</FuuzeText>
            <FuuzeTooltip
              render={
                <FuuzeTooltipDefaultTrigger
                  hideHelpIcon
                  text={<IoWarning fill={FuuzeColors.orange.orange80} size={20} />}
                />
              }
            >
              {t('stripeMissingDocumentsWarningTooltip')}
            </FuuzeTooltip>
          </div>
        ) : (
          t('dashboard')
        )
      }
    />
  );

  const CreatorTiersLink = () => (
    <FuuzeNavLink
      path='/creator/tiers'
      active={pathname.includes('/creator/tiers')}
      Icon={PiCardsThreeBold}
      title={t('tiers')}
    />
  );

  const CreatorStudioLink = () => (
    <FuuzeNavLink
      path='/creator/studio'
      active={pathname.includes('/creator/studio')}
      Icon={LuAudioLines}
      title={t('studio')}
    />
  );

  const onAddPodcast = () => {
    navigate('/becomeCreator');
    closeMenu();
  };

  if (size === 'desktop' || size === 'xl') {
    return (
      <nav style={{ flex: 1 }}>
        <List>
          <li>
            <SearchNavLink title={t('search')} />
          </li>
          <li>
            <LibraryLink />
          </li>
          <li>
            <NavBarSettingsLink />
          </li>

          {isUserCreator ? (
            <>
              <FlexColumn>
                <Divider />
                <small style={{ color: FuuzeColors.orange.orange100 }}>{t('creatorZone')}</small>
              </FlexColumn>

              <PodcastSelector user={user.data!} onAddPodcast={onAddPodcast} />
              <li>
                <CreatorDashboardLink />
              </li>
              <li>
                <CreatorTiersLink />
              </li>
              <li>
                <NavBarCreatorPodcastLink />
              </li>
              <li>
                <CreatorStudioLink />
              </li>
            </>
          ) : (
            <li style={{ marginTop: 32 }}>
              <BecomeCreatorLink />
            </li>
          )}

          <li style={{ marginTop: 'auto', marginLeft: 'auto', marginRight: 'auto' }}>
            <SignOutOrSignInButton />
          </li>
        </List>
      </nav>
    );
  }

  return (
    <FlexColumn style={{ overflowY: 'auto', height: '100%', padding: '0 16px' }}>
      {ready && (
        <>
          <SignOutOrSignInButton style={{ marginTop: 'auto' }} />
          <Divider />
          <nav>
            <List>
              {isUserCreator && (
                <>
                  <small style={{ color: FuuzeColors.orange.orange100 }}>{t('creatorZone')}</small>

                  <PodcastSelector user={user.data!} onAddPodcast={onAddPodcast} />

                  <li onClick={closeMenu}>
                    <CreatorDashboardLink />
                  </li>
                  <li onClick={closeMenu}>
                    <CreatorTiersLink />
                  </li>
                  <li onClick={closeMenu}>
                    <NavBarCreatorPodcastLink />
                  </li>
                  <li onClick={closeMenu}>
                    <CreatorStudioLink />
                  </li>
                </>
              )}

              {!isUserCreator && (
                <li onClick={closeMenu} style={{ marginTop: 32 }}>
                  <BecomeCreatorLink />
                </li>
              )}

              <Divider />

              <li onClick={closeMenu}>
                <NavBarSettingsLink />
              </li>

              <li onClick={closeMenu}>
                <LibraryLink />
              </li>

              <li onClick={closeMenu}>
                <SearchNavLink title={t('search')} />
              </li>
            </List>
          </nav>
        </>
      )}
    </FlexColumn>
  );
}

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
  height: 100%;

  li {
    list-style: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${props => props.theme.colors.dark.dark400};
  margin: 16px 0;
`;
